import React from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { ImagePicker } from "./SlideInspector";

const input = "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none disabled:bg-slate-50";

const Field = ({ label, children }) => (
  <label className="block text-xs font-medium text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const plusDays = (iso, days) => {
  const date = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

// Réglages de la proposition : chiffrage lié, référence, dates, conditions, logo client, modèle client.
const ProposalSettings = ({
  meta, editable, estimates, estimateId, onEstimate, onMeta, clientProfile, onTemplate, onClientLogo, onClose,
}) => {
  const set = (field) => (event) => onMeta({ [field]: event.target.value || null }, `meta.${field}`);
  return (
    <aside className="w-full shrink-0 space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:w-80">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Réglages de l'offre</h2>
        <button type="button" onClick={onClose} aria-label="Fermer les réglages" className="rounded p-1 text-slate-400 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>

      <Field label="Chiffrage lié (prix et délais)">
        <select className={input} disabled={!editable} value={estimateId || ""} onChange={(event) => onEstimate(Number(event.target.value))}>
          {!estimateId && <option value="">Aucun</option>}
          {estimates.map((estimate) => <option key={estimate.id} value={estimate.id}>{estimate.title}</option>)}
        </select>
      </Field>
      {estimateId && (
        <Link to={`/tools/offres/${estimateId}`} className="block text-xs text-blue-700 hover:underline">Ouvrir le chiffrage</Link>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Référence">
          <input className={input} disabled={!editable} value={meta.reference || ""} maxLength={60} onChange={set("reference")} />
        </Field>
        <Field label="Version">
          <input className={input} disabled={!editable} value={meta.version || ""} maxLength={20} onChange={set("version")} />
        </Field>
        <Field label="Date d'émission">
          <input type="date" className={input} disabled={!editable} value={meta.date || ""} onChange={set("date")} />
        </Field>
        <Field label="Valable jusqu'au">
          <input type="date" className={input} disabled={!editable} value={meta.validity_date || ""} onChange={set("validity_date")} />
        </Field>
      </div>
      {editable && meta.date && (
        <button type="button" className="text-xs text-slate-600 underline"
          onClick={() => onMeta({ validity_date: plusDays(meta.date, 30) })}>
          Validité : 30 jours après l'émission
        </button>
      )}
      <Field label="Conditions de règlement">
        <textarea className={input} rows={3} disabled={!editable} value={meta.payment_terms || ""} maxLength={1000}
          onChange={set("payment_terms")} />
      </Field>
      <div className="space-y-1">
        <span className="text-xs font-medium text-slate-600">Logo du client (couverture)</span>
        {editable
          ? <ImagePicker value={meta.client_logo} label="Logo" onChange={(ref) => onMeta({ client_logo: ref })} />
          : <p className="text-xs text-slate-500">{meta.client_logo ? "Logo ajouté." : "Aucun logo."}</p>}
        {editable && clientProfile && meta.client_logo && meta.client_logo !== clientProfile.logo && (
          <button type="button" className="text-xs text-slate-600 underline" onClick={() => onClientLogo(meta.client_logo)}>
            Garder ce logo pour les prochaines offres {clientProfile.client_name}
          </button>
        )}
        {clientProfile && meta.client_logo && meta.client_logo === clientProfile.logo && (
          <p className="text-xs text-slate-500">Logo enregistré sur la fiche {clientProfile.client_name}.</p>
        )}
      </div>

      {clientProfile && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <h3 className="text-sm font-semibold text-slate-800">Modèle {clientProfile.client_name}</h3>
          <p className="text-xs text-slate-500">
            {clientProfile.has_proposal_template
              ? `Les prochaines propositions pour ce client partiront du modèle enregistré (${clientProfile.proposal_template_slides} slides) ; les slides de lots seront régénérées depuis le chiffrage.`
              : "Aucun modèle : les prochaines propositions suivront l'arc Innowide standard."}
          </p>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => onTemplate(false)}
                className="rounded-full bg-slate-900 px-3 py-1 text-xs text-white">Enregistrer cette offre comme modèle</button>
              {clientProfile.has_proposal_template && (
                <button type="button" onClick={() => onTemplate(true)}
                  className="rounded-full px-3 py-1 text-xs text-slate-600 hover:bg-slate-100">Retirer le modèle</button>
              )}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default ProposalSettings;
